
import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface Milestone {
  id: string;
  type: 'matches' | 'streak' | 'rating';
  title: string;
  description: string;
  icon: string;
  value: number;
  /** The player's actual current value (matches played, streak length or rating) */
  currentTotal?: number;
}

// Milestone thresholds
const MATCH_MILESTONES = [10, 25, 50, 100, 150, 200, 250, 500, 1000];
const STREAK_MILESTONES = [3, 5, 7, 10, 15, 20];
const RATING_MILESTONES = [1200, 1300, 1400, 1500, 1600, 1700, 1800, 1900, 2000];

export const useMilestoneCelebration = () => {
  const { user } = useAuth();
  const [showCelebration, setShowCelebration] = useState(false);
  const [currentMilestone, setCurrentMilestone] = useState<Milestone | null>(null);
  const earnedRef = useRef<Set<string>>(new Set());
  const [earnedLoaded, setEarnedLoaded] = useState(false);

  const localKey = user?.id ? `luvall-milestones-${user.id}` : null;

  // Load earned milestones from the account (localStorage is only an offline fallback)
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const load = async () => {
      // Seed from local cache first so we never re-celebrate while offline
      if (localKey) {
        try {
          const stored = localStorage.getItem(localKey);
          if (stored) (JSON.parse(stored) as string[]).forEach(id => earnedRef.current.add(id));
        } catch {
          /* ignore malformed cache */
        }
      }

      const { data, error } = await supabase
        .from('user_milestones')
        .select('milestone_id')
        .eq('user_id', user.id);

      if (!cancelled) {
        if (!error && data) {
          data.forEach(row => earnedRef.current.add(row.milestone_id));
          if (localKey) {
            localStorage.setItem(localKey, JSON.stringify(Array.from(earnedRef.current)));
          }
        }
        setEarnedLoaded(true);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user?.id, localKey]);

  const persistEarned = useCallback(
    async (milestones: Milestone[]) => {
      if (!user?.id || milestones.length === 0) return;

      milestones.forEach(m => earnedRef.current.add(m.id));
      if (localKey) {
        localStorage.setItem(localKey, JSON.stringify(Array.from(earnedRef.current)));
      }

      const { error } = await supabase.from('user_milestones').upsert(
        milestones.map(m => ({
          user_id: user.id,
          milestone_id: m.id,
          type: m.type,
          value: m.value,
        })),
        { onConflict: 'user_id,milestone_id', ignoreDuplicates: true }
      );

      if (error) console.error('Failed to record milestones:', error);
    },
    [user?.id, localKey]
  );

  // Check for milestones
  const checkMilestones = useCallback(async (
    totalMatches: number,
    currentStreak: number,
    streakType: 'win' | 'loss' | undefined,
    skillRating: number
  ) => {
    if (!user?.id || !earnedLoaded) return;

    const candidates: Milestone[] = [];

    for (const threshold of MATCH_MILESTONES) {
      if (totalMatches >= threshold && !earnedRef.current.has(`matches_${threshold}`)) {
        candidates.push({
          id: `matches_${threshold}`,
          type: 'matches',
          title: `${threshold} Matches Played!`,
          description: `Congratulations! You've played ${threshold} competitive matches. Keep up the great work!`,
          icon: '🎾',
          value: threshold,
          currentTotal: totalMatches,
        });
      }
    }

    if (streakType === 'win' && currentStreak > 0) {
      for (const threshold of STREAK_MILESTONES) {
        if (currentStreak >= threshold && !earnedRef.current.has(`streak_${threshold}`)) {
          candidates.push({
            id: `streak_${threshold}`,
            type: 'streak',
            title: `${threshold} Win Streak!`,
            description: `Amazing! You're on fire with ${threshold} consecutive wins! 🔥`,
            icon: '🔥',
            value: threshold,
            currentTotal: currentStreak,
          });
        }
      }
    }

    for (const threshold of RATING_MILESTONES) {
      if (skillRating >= threshold && !earnedRef.current.has(`rating_${threshold}`)) {
        candidates.push({
          id: `rating_${threshold}`,
          type: 'rating',
          title: `${threshold} Rating Achieved!`,
          description: `Outstanding! You've reached a skill rating of ${threshold}. You're becoming a pro!`,
          icon: '⭐',
          value: threshold,
          currentTotal: Math.round(skillRating),
        });
      }
    }

    if (candidates.length === 0) return;

    // Highest newly crossed milestone per type
    const highestByType: Record<string, Milestone> = {};
    for (const milestone of candidates) {
      if (!highestByType[milestone.type] || milestone.value > highestByType[milestone.type].value) {
        highestByType[milestone.type] = milestone;
      }
    }

    // Show one: rating > streak > matches. Every crossed milestone is recorded.
    const priority: Milestone['type'][] = ['rating', 'streak', 'matches'];
    const shown = priority.map(t => highestByType[t]).find(Boolean);

    await persistEarned(candidates);

    if (shown) {
      setCurrentMilestone(shown);
      setShowCelebration(true);
    }
  }, [user?.id, earnedLoaded, persistEarned]);

  // Trigger celebration after match completion
  const triggerPostMatchCheck = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { data: core, error } = await supabase
        .rpc('get_player_stats_optimized', {
          p_user_id: user.id,
          p_year: null
        })
        .maybeSingle();

      if (error) throw error;

      if (core) {
        await checkMilestones(
          Number(core.total_matches ?? 0),
          Number(core.current_streak ?? 0),
          (core as any).streak_type ?? (core as any).current_streak_type,
          Number(core.skill_rating ?? 1000)
        );
      }
    } catch (error) {
      console.error('Error checking milestones:', error);
    }
  }, [user?.id, checkMilestones]);

  const closeCelebration = useCallback(() => {
    setShowCelebration(false);
    setCurrentMilestone(null);
  }, []);

  return {
    showCelebration,
    currentMilestone,
    closeCelebration,
    checkMilestones,
    triggerPostMatchCheck,
  };
};
